/*
 * av-bundle-builder.js — Bundle & Save page controller (concept A: two modes)
 * -----------------------------------------------------------------------------
 * Drives sections/avanelle-bundle-builder.liquid. A "Single Flavor | Bundle &
 * Save" switch picks the mode:
 *
 *  - Single Flavor: pick a flavour, pick a row of the PDP's own 1 / 2+1 / 3+2
 *    ladder. The cart lines are the PDP's control-arm lines exactly (a paid
 *    line on the row's plan + an _av_free line for the free bottles).
 *  - Bundle & Save: pick a size (2/4/6), fill the box by tapping flavours or a
 *    one-tap mix. The size carries the selling plan; no cadence is printed.
 *
 * Everything money is server-rendered into the [data-av-config] island (same
 * discipline as av-buybox.js): this file does no money math and parses no money
 * string. It only swaps pre-formatted strings.
 *
 * Bundle cart contract (mirrors av-buybox.js's duo path — see the SELECTORS,
 * NEVER VALUES note there): one paid line per flavour, all sharing one
 * _av_bundle_id, each claiming _av_offer (the compiled offer handle) and
 * _av_tier ("mix" + size), with selling_plan attached on the Autoship path
 * only. The pricing engine (extensions/bundle-engine, sibling repo) re-derives
 * everything and uses the claims only to disambiguate.
 */
(function () {
  'use strict';
  if (window.AvBundleBuilder) return;

  function cfgOf(root) {
    var el = root.querySelector('[data-av-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function fmt(template, key, value) {
    return String(template || '').replace('__' + key + '__', value);
  }

  function sameBox(a, b) {
    return a.length === b.length && a.slice().sort().join() === b.slice().sort().join();
  }

  function mount(root) {
    if (!root || root.dataset.avMounted === '1') return;
    root.dataset.avMounted = '1';
    var cfg = cfgOf(root);
    var SIZES = cfg.sizes || {};
    var FLAVORS = cfg.flavors || [];
    var STR = cfg.strings || {};
    var SINGLE = cfg.single || {};
    var TIERS = SINGLE.tiers || [];
    var CART_ADD_URL = cfg.cartAddUrl || '/cart/add.js';
    var ROOT_URL = cfg.rootUrl || '/';
    var CHECKOUT_URL = (ROOT_URL === '/' ? '' : ROOT_URL) + '/checkout';
    var OFFER_HANDLE = cfg.offerHandle || '';
    var BASE_ACCENT = root.style.getPropertyValue('--av-accent') || '';

    var flavorByKey = {};
    var keyByToken = {};
    FLAVORS.forEach(function (f) { flavorByKey[f.key] = f; if (f.token) keyByToken[f.token] = f.key; });
    var tierById = {};
    TIERS.forEach(function (t) { tierById[t.id] = t; });

    /* One-tap mixes, three per size: "size|name|flavour,flavour,…" per line. A
       mix is dropped when its bottle count is not its size or when any flavour
       is out of this market's catalog (absent from FLAVORS), so a mix can never
       promise a jar the page cannot sell. */
    var MIXES = {};
    String(cfg.mixPresets || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 3) return;
      var n = parseInt(parts[0], 10);
      var name = parts[1].trim();
      var keys = parts[2].split(',').map(function (t) { return keyByToken[t.trim()]; });
      if (!SIZES[n] || !name || keys.length !== n || keys.some(function (k) { return !k; })) return;
      (MIXES[n] = MIXES[n] || []).push({ name: name, box: keys });
    });

    /* ------------------------------- state ---------------------------------- */
    var mode = cfg.mode === 'single' ? 'single' : 'bundle';
    var singleFlavor = FLAVORS.length ? FLAVORS[0].key : '';
    var singleTier = tierById[SINGLE.default] ? SINGLE.default : (TIERS[0] ? TIERS[0].id : '');
    var size = parseInt(root.getAttribute('data-default-size'), 10) || 4;
    if (!SIZES[size]) size = parseInt(Object.keys(SIZES)[0], 10);
    /* The box starts EMPTY; a mix fills it in one tap and every bottle stays
       swappable. Changing size keeps the first picks that still fit. */
    var slots = [];

    /* -------------------------------- refs ---------------------------------- */
    function all(sel) { return [].slice.call(root.querySelectorAll(sel)); }
    var modeBtns = all('[data-mode-btn]');
    var modePanels = all('[data-mode-panel]');
    var swatches = all('[data-single-flavor]');
    var tierBtns = all('[data-single-tier]');
    var tierImgs = all('[data-tier-img]');
    var chips = all('[data-size-chip]');
    var slotEls = all('[data-slot]');
    var slotsEl = root.querySelector('[data-slots]');
    var tiles = all('[data-flavor-tile]');
    var hintEl = root.querySelector('[data-pick-hint]');
    var flowEl = root.querySelector('[data-flow]');
    var flowTextEl = root.querySelector('[data-flow-text]');
    var mixesWrap = root.querySelector('[data-mixes-wrap]');
    var mixesEl = root.querySelector('[data-mixes]');
    var packsToggle = root.querySelector('[data-packs-toggle]');
    var packsPicked = root.querySelector('[data-packs-picked]');
    var sumSubEl = root.querySelector('[data-sum-sub]');
    var sumNowEl = root.querySelector('[data-sum-now]');
    var sumWasEl = root.querySelector('[data-sum-was]');
    var sumSaveEl = root.querySelector('[data-sum-save]');
    var atcBtn = root.querySelector('[data-builder-atc]');
    var otpLink = root.querySelector('[data-otp-link]');
    var otpWasEl = root.querySelector('[data-otp-was]');
    var otpNowEl = root.querySelector('[data-otp-now]');
    var otpDayEl = root.querySelector('[data-otp-day]');
    var otpLabelEl = root.querySelector('[data-otp-label]');
    var otpErrorEl = root.querySelector('[data-otp-error]');
    var otpLabelText = otpLabelEl ? otpLabelEl.textContent : '';

    /* ------------------------------- mixes ---------------------------------- */
    /* The jars a card shows: each flavour once, in the mix's own order. When a
       five-flavour mix doubles one flavour, that one stands in the middle. */
    function artOf(box) {
      var uniq = [], cnt = {};
      box.forEach(function (k) { cnt[k] = (cnt[k] || 0) + 1; if (uniq.indexOf(k) < 0) uniq.push(k); });
      if (uniq.length === 5) {
        var extra = uniq.filter(function (k) { return cnt[k] > 1; });
        if (extra.length === 1) { uniq.splice(uniq.indexOf(extra[0]), 1); uniq.splice(2, 0, extra[0]); }
      }
      return uniq;
    }
    /* Pack art in the language of the live 3- and 5-jar photos: the middle jar in
       front, each step outward smaller and set back (higher). One entry per jar,
       [x in front-jar widths, scale, lift in jar heights, z]. Two-flavour packs
       stand as an even pair. */
    var PACK_LAYOUT = {
      2: [[-0.5, 1, 0, 2], [0.5, 1, 0, 2]],
      3: [[-0.64, 0.87, 0.045, 1], [0, 1, 0, 3], [0.64, 0.87, 0.045, 1]],
      /* two even front jars (no overlap), the outer pair smaller and set back */
      4: [[-1.0, 0.84, 0.05, 1], [-0.5, 1, 0, 3], [0.5, 1, 0, 3], [1.0, 0.84, 0.05, 1]],
      5: [[-1.14, 0.74, 0.09, 1], [-0.64, 0.87, 0.045, 2], [0, 1, 0, 3], [0.64, 0.87, 0.045, 2], [1.14, 0.74, 0.09, 1]]
    };
    var mixBtns = [];
    function buildMixes() {
      mixBtns = [];
      if (!mixesEl) return;
      mixesEl.textContent = '';
      (MIXES[size] || []).forEach(function (m) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'av-bsec__mix';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', fmt(STR.mixAria, 'NAME', m.name));
        var tick = document.createElement('span');
        tick.className = 'av-bsec__mix-tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.innerHTML = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.2 5 8.7l4.5-5"/></svg>';
        var art = document.createElement('span');
        var jars = artOf(m.box);
        var lay = PACK_LAYOUT[jars.length] || PACK_LAYOUT[2];
        art.className = 'av-bsec__mix-art av-bsec__mix-art--' + jars.length;
        art.setAttribute('aria-hidden', 'true');
        jars.forEach(function (k, i) {
          var L = lay[i] || lay[lay.length - 1];
          var j = document.createElement('span');
          j.className = 'av-bsec__pj';
          j.style.cssText = '--x:' + L[0] + ';--s:' + L[1] + ';--lift:' + L[2] + ';z-index:' + L[3] + ';background-image:url("' + flavorByKey[k].jar + '")';
          art.appendChild(j);
        });
        var name = document.createElement('span');
        name.className = 'av-bsec__mix-name';
        name.textContent = m.name;
        b.appendChild(tick); b.appendChild(art); b.appendChild(name);
        b.addEventListener('click', function () { slots = m.box.slice(); render(); });
        mixesEl.appendChild(b);
        mixBtns.push({ el: b, box: m.box, name: m.name });
      });
      if (mixesWrap) mixesWrap.hidden = mixBtns.length === 0;
    }

    /* ------------------------------- render --------------------------------- */
    function render() {
      var single = mode === 'single';
      var p = SIZES[size] || {};
      var full = slots.length === size;
      var t = tierById[singleTier] || {};
      var sf = flavorByKey[singleFlavor] || FLAVORS[0] || {};

      modeBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-mode-btn') === mode ? 'true' : 'false'); });
      modePanels.forEach(function (el) { el.hidden = el.getAttribute('data-mode-panel') !== mode; });
      /* Single Flavor wears the picked flavour's colour, like its PDP. */
      if (single && sf.accent) root.style.setProperty('--av-accent', sf.accent);
      else if (BASE_ACCENT) root.style.setProperty('--av-accent', BASE_ACCENT);

      /* ---- single ---- */
      swatches.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-single-flavor') === singleFlavor ? 'true' : 'false'); });
      tierBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-single-tier') === singleTier ? 'true' : 'false'); });
      tierImgs.forEach(function (img) {
        var u = parseInt(img.getAttribute('data-tier-img'), 10);
        var src = u >= 5 ? sf.t5 : (u >= 3 ? sf.t3 : sf.t1);
        if (src && img.getAttribute('src') !== src) img.src = src;
      });

      /* ---- bundle ---- */
      chips.forEach(function (c) {
        c.setAttribute('aria-pressed', parseInt(c.getAttribute('data-size-chip'), 10) === size ? 'true' : 'false');
      });
      if (slotsEl) slotsEl.setAttribute('data-size', String(size));
      slotEls.forEach(function (el, i) {
        var img = el.querySelector('[data-slot-img]');
        if (i >= size) { el.hidden = true; return; }
        el.hidden = false;
        var f = slots[i] && flavorByKey[slots[i]];
        el.classList.toggle('is-filled', !!f);
        el.classList.toggle('is-empty', !f);
        el.classList.toggle('is-next', !f && i === slots.length);
        /* An empty spot is not a control: disabled and hidden from assistive tech. */
        el.disabled = !f;
        if (f) {
          if (img) { img.src = f.jar || f.img; img.alt = ''; img.hidden = false; }
          el.removeAttribute('aria-hidden');
          el.setAttribute('aria-label', fmt(STR.removeFlavor, 'FLAVOR', f.name));
        } else {
          if (img) { img.removeAttribute('src'); img.hidden = true; }
          el.setAttribute('aria-hidden', 'true');
          el.removeAttribute('aria-label');
        }
      });
      tiles.forEach(function (tl) {
        var key = tl.getAttribute('data-flavor-tile');
        var cnt = slots.filter(function (s) { return s === key; }).length;
        var badge = tl.querySelector('[data-tile-count]');
        var plus = tl.querySelector('[data-tile-plus]');
        if (badge) { badge.textContent = String(cnt); badge.hidden = cnt === 0; }
        if (plus) plus.hidden = full;
        tl.classList.toggle('is-maxed', full);
        tl.classList.toggle('is-dim', full && cnt === 0);
      });
      if (hintEl) {
        hintEl.textContent = full ? (STR.boxFull || '') : fmt(STR.pickMore, 'COUNT', size - slots.length);
        hintEl.classList.toggle('is-short', !full);
      }
      /* The hint under the box guides until it is full, then gets out of the way. */
      if (flowEl) flowEl.hidden = full;
      if (flowTextEl) flowTextEl.textContent = slots.length ? fmt(STR.addMore, 'COUNT', size - slots.length) : (STR.hintStart || '');
      var pickedName = '';
      mixBtns.forEach(function (m) {
        var on = sameBox(slots, m.box);
        m.el.classList.toggle('is-sel', on);
        m.el.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) pickedName = m.name;
      });
      /* Collapsed, the header still names the picked pack. */
      if (packsPicked) packsPicked.textContent = pickedName;
      if (sumSubEl) sumSubEl.textContent = p.sub ? p.sub.dayAutoship : '';
      if (sumNowEl) sumNowEl.textContent = p.sub ? p.sub.now : '';
      if (sumWasEl) sumWasEl.textContent = p.sub ? p.sub.was : '';
      if (sumSaveEl && p.sub) sumSaveEl.textContent = fmt(STR.saveFmt, 'PCT', p.sub.save);

      /* ---- shared buy buttons ---- */
      var ready = single ? !!(t.id && sf.variantId) : full;
      if (atcBtn) {
        atcBtn.classList.toggle('is-disabled', !ready);
        atcBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      }
      var o = single ? t.otp : p.otp;
      if (o) {
        if (otpWasEl) { otpWasEl.textContent = o.was; otpWasEl.hidden = o.was === o.now; }
        if (otpNowEl) otpNowEl.textContent = o.now;
        if (otpDayEl) otpDayEl.textContent = fmt(STR.otpPerDayFmt, 'PRICE', o.day);
      }
    }

    /* ------------------------------- wiring --------------------------------- */
    modeBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var m = b.getAttribute('data-mode-btn');
        if (m === mode) return;
        mode = m;
        if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
        render();
      });
    });
    /* Favorite packs start collapsed; the toggle opens and closes them. */
    if (packsToggle && mixesEl) {
      packsToggle.addEventListener('click', function () {
        var open = packsToggle.getAttribute('aria-expanded') !== 'true';
        packsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        mixesEl.hidden = !open;
      });
    }
    swatches.forEach(function (b) {
      b.addEventListener('click', function () { singleFlavor = b.getAttribute('data-single-flavor'); render(); });
    });
    tierBtns.forEach(function (b) {
      b.addEventListener('click', function () { singleTier = b.getAttribute('data-single-tier'); render(); });
    });
    chips.forEach(function (c) {
      c.addEventListener('click', function () {
        var n = parseInt(c.getAttribute('data-size-chip'), 10);
        if (!SIZES[n] || n === size) return;
        size = n;
        slots = slots.slice(0, n);
        buildMixes();
        render();
      });
    });
    slotEls.forEach(function (el, i) {
      el.addEventListener('click', function () {
        if (i >= slots.length) return;
        slots.splice(i, 1);
        render();
      });
    });
    tiles.forEach(function (tl) {
      tl.addEventListener('click', function () {
        if (slots.length >= size) return;
        slots.push(tl.getAttribute('data-flavor-tile'));
        render();
      });
    });

    /* --------------------------- shared cart POST ---------------------------- */
    function newBundleId() { return 'av' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36); }
    function buildBundleItems(subscribe) {
      var p = SIZES[size] || {};
      var plan = p.plan || '';
      var bundleId = newBundleId();
      var qtyByKey = {};
      slots.forEach(function (k) { qtyByKey[k] = (qtyByKey[k] || 0) + 1; });
      return Object.keys(qtyByKey).map(function (k) {
        var f = flavorByKey[k];
        var props = { _av_bundle: p.label || 'Bundle', _av_bundle_id: bundleId, _av_plan: plan };
        /* SELECTORS, NEVER VALUES — see av-buybox.js. "mix" + size is exactly how
           the compiled offer's bars are authored (mix2 / mix4 / mix6). */
        if (OFFER_HANDLE) {
          props._av_tier = 'mix' + size;
          props._av_offer = OFFER_HANDLE;
        }
        var item = { id: Number(f.variantId), quantity: qtyByKey[k], properties: props };
        if (subscribe && plan) item.selling_plan = Number(plan);
        return item;
      });
    }
    /* The PDP's control-arm single-flavour lines, unchanged: the paid bottles on
       the row's plan, the free ones as a separate _av_free line (no plan). */
    function buildSingleItems(subscribe) {
      var t = tierById[singleTier];
      var f = flavorByKey[singleFlavor];
      if (!t || !f) return [];
      var bundleId = newBundleId();
      var items = [];
      var paid = { id: Number(f.variantId), quantity: t.paid, properties: { _av_bundle: t.title || 'Bundle', _av_bundle_id: bundleId, _av_plan: t.plan || '' } };
      if (subscribe && t.plan) paid.selling_plan = Number(t.plan);
      items.push(paid);
      if (t.free > 0) items.push({ id: Number(f.variantId), quantity: t.free, properties: { _av_bundle: t.title || 'Bundle', _av_bundle_id: bundleId, _av_free: 'true' } });
      return items;
    }
    function buildItems(subscribe) { return mode === 'single' ? buildSingleItems(subscribe) : buildBundleItems(subscribe); }
    function canBuy() { return mode === 'single' ? buildSingleItems(false).length > 0 : slots.length === size; }
    /* Cart slider test, arm B (slider1 = b, snippets/av-pack.liquid): open the slider at once
       and fill it when the add lands, instead of going to checkout (as av-buybox.js). */
    function sliderNow() {
      if (!window.avTests || window.avTests.slider1 !== 'b') return null;
      var drawer = document.querySelector('cart-drawer');
      return drawer && typeof drawer.openWhileAdding === 'function' ? drawer.openWhileAdding() : null;
    }
    /* Re-render the slider with the cart as it is now and open it (as av-buybox.js). Null
       where there is no slider on the page. */
    function openCartSlider() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.refreshCartDrawer !== 'function' || typeof drawer.toggleDrawerClasses !== 'function') return null;
      return Promise.resolve(drawer.refreshCartDrawer()).then(function () {
        if (!drawer.isOpen) drawer.toggleDrawerClasses();
      });
    }
    function toCheckout() {
      if (window.avCheckoutMark) window.avCheckoutMark();
      window.location.href = CHECKOUT_URL;
    }
    function submitAdd(items, btn, onError, onSlider) {
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      function release() { btn.disabled = false; btn.removeAttribute('aria-busy'); }
      var early = sliderNow();
      return fetch(CART_ADD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) { if (early) early.fail(); release(); onError(res.description || res.message); return null; }
          /* Pin any running A/B arm on the cart before leaving (av-buybox.js does the
             same); each resolves on failure too, so it never blocks the add. */
          return Promise.resolve(window.avTestsRemember ? window.avTestsRemember() : null)
            .then(function () { return window.avExpRemember ? window.avExpRemember() : null; })
            .then(function () { return res; });
        })
        .then(function (res) {
          if (!res) return;
          if (early) return early.done().then(function () { release(); if (onSlider) onSlider(); });
          /* A cart that has been to checkout already opens the slider instead, in both arms
             (snippets/av-pack.liquid). */
          var slider = window.avCheckoutSeen && window.avCheckoutSeen() ? openCartSlider() : null;
          if (slider) {
            return slider.then(function () { release(); if (onSlider) onSlider(); }, toCheckout);
          }
          toCheckout();
        })
        .catch(function (err) { if (early) early.fail(); release(); onError(err); });
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', function () {
        if (!canBuy() || atcBtn.disabled) return;
        submitAdd(buildItems(true), atcBtn, function (err) { console.warn('[av-builder] add error:', err); });
      });
    }
    if (otpLink) {
      otpLink.addEventListener('click', function () {
        if (!canBuy() || otpLink.disabled) return;
        if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
        if (otpLabelEl && STR.otpAdding) otpLabelEl.textContent = STR.otpAdding;
        submitAdd(buildItems(false), otpLink, function () {
          if (otpLabelEl) otpLabelEl.textContent = otpLabelText;
          if (otpErrorEl) { otpErrorEl.textContent = STR.otpError || ''; otpErrorEl.hidden = false; }
        }, function () {
          if (otpLabelEl) otpLabelEl.textContent = otpLabelText;
        });
      });
    }

    /* ------------------------------ gallery ---------------------------------- */
    var imgs = [].slice.call(root.querySelectorAll('.av-bsec__gimg'));
    var thumbs = [].slice.call(root.querySelectorAll('[data-bsec-thumb]'));
    var counter = root.querySelector('[data-bsec-counter]');
    var gidx = 0;
    function gshow(n) {
      if (!imgs.length) return;
      gidx = (n + imgs.length) % imgs.length;
      imgs.forEach(function (im, i) { im.classList.toggle('is-active', i === gidx); });
      thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === gidx); });
      if (counter) counter.textContent = fmt(fmt(STR.counterFmt, 'CUR', gidx + 1), 'TOT', imgs.length);
    }
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { gshow(i); }); });
    var gPrev = root.querySelector('[data-bsec-prev]');
    var gNext = root.querySelector('[data-bsec-next]');
    if (gPrev) gPrev.addEventListener('click', function () { gshow(gidx - 1); });
    if (gNext) gNext.addEventListener('click', function () { gshow(gidx + 1); });

    /* ------------------------- gallery zoom lightbox ------------------------- */
    /* The PDP's interaction grammar (av-buybox.js), ported: stage click/Enter
       opens, wheel and double-click zoom, drag pans while zoomed, pinch works
       on touch, swipe navigates at rest, Escape closes. */
    var stage = root.querySelector('[data-bsec-stage]');
    var gModal = root.querySelector('[data-bsec-modal]');
    var gZoom = root.querySelector('[data-bsec-zoom-img]');
    var gmCounter = root.querySelector('[data-bsec-modal-counter]');
    if (stage && gModal && gZoom) {
      var zs = 1, ztx = 0, zty = 0;
      var applyZoom = function () {
        gZoom.style.transform = 'translate(' + ztx + 'px,' + zty + 'px) scale(' + zs + ')';
        gZoom.style.cursor = zs > 1 ? 'grab' : 'zoom-in';
      };
      var resetZoom = function () { zs = 1; ztx = 0; zty = 0; gZoom.style.transition = 'none'; applyZoom(); };
      var clampPan = function () {
        var mx = gZoom.clientWidth * (zs - 1) / 2, my = gZoom.clientHeight * (zs - 1) / 2;
        ztx = Math.max(-mx, Math.min(mx, ztx)); zty = Math.max(-my, Math.min(my, zty));
      };
      var zoomAt = function (factor, cx, cy) {
        var r = gZoom.getBoundingClientRect();
        var ox = cx - (r.left + r.width / 2), oy = cy - (r.top + r.height / 2);
        var ns = Math.max(1, Math.min(4, zs * factor)); var k = ns / zs;
        ztx = ox - (ox - ztx) * k; zty = oy - (oy - zty) * k; zs = ns;
        if (zs === 1) { ztx = 0; zty = 0; } clampPan();
        gZoom.style.transition = 'transform .12s ease'; applyZoom();
      };
      var syncModalImage = function () {
        var a = imgs[gidx];
        if (a) gZoom.src = a.currentSrc || a.src;
        resetZoom();
        if (gmCounter) gmCounter.textContent = fmt(fmt(STR.counterFmt, 'CUR', gidx + 1), 'TOT', imgs.length);
      };
      var modalNav = function (d) { gshow(gidx + d); syncModalImage(); };
      var openG = function () { gModal.hidden = false; document.body.style.overflow = 'hidden'; syncModalImage(); };
      var closeG = function () { gModal.hidden = true; document.body.style.overflow = ''; resetZoom(); };

      stage.addEventListener('click', openG);
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openG(); }
      });
      [].slice.call(gModal.querySelectorAll('[data-bsec-close]')).forEach(function (b) {
        b.addEventListener('click', closeG);
      });
      var gmPrev = gModal.querySelector('[data-bsec-modal-prev]');
      var gmNext = gModal.querySelector('[data-bsec-modal-next]');
      if (gmPrev) gmPrev.addEventListener('click', function (e) { e.stopPropagation(); modalNav(-1); });
      if (gmNext) gmNext.addEventListener('click', function (e) { e.stopPropagation(); modalNav(1); });
      document.addEventListener('keydown', function (e) {
        if (gModal.hidden) return;
        if (e.key === 'Escape') closeG();
        else if (e.key === 'ArrowLeft') { e.preventDefault(); modalNav(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); modalNav(1); }
      });

      gZoom.addEventListener('wheel', function (e) {
        e.preventDefault();
        zoomAt(e.deltaY < 0 ? 1.18 : 0.85, e.clientX, e.clientY);
      }, { passive: false });
      gZoom.addEventListener('dblclick', function (e) {
        e.preventDefault();
        if (zs > 1) resetZoom(); else zoomAt(2.5, e.clientX, e.clientY);
      });
      var _swx = null;
      gZoom.addEventListener('touchstart', function (e) {
        _swx = (zs === 1 && e.touches.length === 1) ? e.touches[0].clientX : null;
      }, { passive: true });
      gZoom.addEventListener('touchend', function (e) {
        if (_swx === null || zs !== 1) { _swx = null; return; }
        var dx = e.changedTouches[0].clientX - _swx;
        if (Math.abs(dx) > 50) modalNav(dx < 0 ? 1 : -1);
        _swx = null;
      }, { passive: true });
      var dragging = false, lx = 0, ly = 0, pts = {}, pinchD = 0;
      gZoom.addEventListener('pointerdown', function (e) {
        pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var n = Object.keys(pts).length;
        if (n === 1) { dragging = zs > 1; lx = e.clientX; ly = e.clientY; try { gZoom.setPointerCapture(e.pointerId); } catch (err) {} }
        else if (n === 2) {
          dragging = false;
          var pp = Object.keys(pts).map(function (k) { return pts[k]; });
          pinchD = Math.hypot(pp[0].x - pp[1].x, pp[0].y - pp[1].y);
        }
      });
      gZoom.addEventListener('pointermove', function (e) {
        if (pts[e.pointerId]) pts[e.pointerId] = { x: e.clientX, y: e.clientY };
        var keys = Object.keys(pts);
        if (keys.length === 2) {
          var pp = keys.map(function (k) { return pts[k]; });
          var d = Math.hypot(pp[0].x - pp[1].x, pp[0].y - pp[1].y);
          if (pinchD) zoomAt(d / pinchD, (pp[0].x + pp[1].x) / 2, (pp[0].y + pp[1].y) / 2);
          pinchD = d;
        } else if (dragging) {
          ztx += e.clientX - lx; zty += e.clientY - ly; lx = e.clientX; ly = e.clientY;
          clampPan(); gZoom.style.transition = 'none'; applyZoom();
        }
      });
      var endPtr = function (e) {
        delete pts[e.pointerId];
        if (Object.keys(pts).length < 2) pinchD = 0;
        if (!Object.keys(pts).length) dragging = false;
      };
      gZoom.addEventListener('pointerup', endPtr);
      gZoom.addEventListener('pointercancel', endPtr);
    }

    /* ------------------------- nutrition lightbox ---------------------------- */
    var nModal = root.querySelector('[data-nutrition-modal]');
    if (nModal) {
      root.querySelectorAll('[data-nutrition-open]').forEach(function (b) {
        b.addEventListener('click', function () { nModal.hidden = false; document.body.style.overflow = 'hidden'; });
      });
      nModal.querySelectorAll('[data-nutrition-close]').forEach(function (b) {
        b.addEventListener('click', function () { nModal.hidden = true; document.body.style.overflow = ''; });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !nModal.hidden) { nModal.hidden = true; document.body.style.overflow = ''; }
      });
    }

    /* ---------------------------- geo alignment ------------------------------ */
    /* Availability is a property of the MARKET, and the market is chosen by URL
       path — a Berlin visitor on the root URL is browsing the US catalog and
       would see five flavours she cannot buy. This store runs no geolocation
       app, so this page aligns itself: ask Shopify's own detector for the
       visitor's country and, when it differs from the page's market country,
       submit the localization form the section rendered — Shopify redirects to
       the right market path and every market-gated thing (tiles, gallery,
       prices, currency, duties row) follows. Guards, in order: one attempt per
       session; only on a fresh landing (an on-site referrer means the visitor
       is already browsing a context — switching under them mid-session would
       fight any deliberate market choice, and Shopify's localization cookie
       cannot arbitrate: it is set on EVERY page render, measured, so it says
       nothing about intent); never to a country the shop does not serve (it
       must appear in the detector's own options list). */
    (function geoAlign() {
      var form = document.getElementById('av-bsec-loc');
      var countryInput = form && form.querySelector('[data-bsec-country]');
      var current = cfg.country || '';
      if (!form || !countryInput || !current) return;
      var KEY = 'av-bb-geo-checked';
      try {
        if (sessionStorage.getItem(KEY)) return;
        sessionStorage.setItem(KEY, '1');
      } catch (e) { return; }
      if (document.referrer) {
        var ref = document.createElement('a');
        ref.href = document.referrer;
        if (ref.hostname === location.hostname) return;
      }
      fetch(ROOT_URL + 'browsing_context_suggestions.json?country%5Benabled%5D=true', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var det = d && d.detected_values && d.detected_values.country;
          if (!det || !det.handle || det.handle === current) return;
          var part = d.suggestions && d.suggestions[0] && d.suggestions[0].parts && d.suggestions[0].parts.country;
          var options = (part && part.options) || {};
          if (!options[det.handle]) return;
          countryInput.value = det.handle;
          form.submit();
        })
        .catch(function () {});
    })();

    /* -------------------------- bfcache recovery ----------------------------- */
    window.addEventListener('pageshow', function () {
      if (atcBtn) { atcBtn.disabled = false; atcBtn.removeAttribute('aria-busy'); }
      if (otpLink) {
        otpLink.disabled = false;
        otpLink.removeAttribute('aria-busy');
        if (otpLabelEl && otpLabelText != null) otpLabelEl.textContent = otpLabelText;
        if (otpErrorEl) { otpErrorEl.hidden = true; otpErrorEl.textContent = ''; }
      }
      render();
    });

    buildMixes();
    render();
  }

  window.AvBundleBuilder = { mount: mount };
  function boot() { [].slice.call(document.querySelectorAll('[data-av-bbuilder]')).forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
